// React import
import React, { useState } from 'react';

// Import MUI
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { cyan, red } from '@mui/material/colors';

// Generic import
import _ from 'lodash';
import axios from 'axios';
import queryString from 'query-string';
import { formatDisplayDateTime } from '../utils/date.js';

// Local import
import readableId from '../ReadableId.js';
import INCOTERMS from '../utils/Incoterms.js';

function OfferOpportunityContract(props) {
    const { org, html, opportunity, status, setStatus, account, print, temporaryAccesstoken } = props;
    const parsed = queryString.parse(window.location.search);

    const [ saving, setSaving ] = useState(false);
    const [ saveSuccess, setSaveSuccess ] = useState(false);
    const [ saveError, setSaveError ] = useState(null);
    const [ showRejectDialog, setShowRejectDialog ] = useState(false);
    const [ rejectReason, setRejectReason ] = useState(null);
    const [ rejectReasonText, setRejectReasonText ] = useState('');

    async function onAccept() {
        setSaveError(null);
        setSaving(true);

        try {
            await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${opportunity.organizationId}&temporaryAccesstoken=${temporaryAccesstoken}`, {
                status: 'endcustomer-accepted',
            });

            setStatus('endcustomer-accepted');
            setSaving(false);
            setSaveSuccess(true);
        } catch(e) {
            setSaveError('generic');
            setSaving(false);
        }
    }

    function onReject() {
        setRejectReason(null);
        setRejectReasonText('');
        setShowRejectDialog(true);
    }

    async function onRejectOK() {
        setSaving(true)
        setShowRejectDialog(false);
        try {
            await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${opportunity.organizationId}&temporaryAccesstoken=${temporaryAccesstoken}`, {
                status: 'endcustomer-rejected',
                selected: [],
                reason: rejectReason === 'Other' ? rejectReasonText : rejectReason
            });
            setStatus('endcustomer-rejected');
            setSaveSuccess(true);
            setSaveError(null);
            setSaving(false);
        } catch (e) {
            console.log(e);
            setSaveError('generic');
            setSaveSuccess(false);
            setSaving(false);
        }
    }

    function onSnackbarClose() {
        setSaveError(null);
        setSaveSuccess(false);
    }

    return (
        <div className='offer-page'>
            {parsed.type === 'preview' && (
                <>
                    <Alert severity='warning'>
                        Note: This is a preview page. Please do not send its link to anyone else.
                    </Alert>
                    <div style={{ position: 'fixed', top: '0px', right: '0px', left: '0px', bottom: '0px', fontSize: '96px', zIndex: 10, transformOrigin: 'center', transform: 'rotate(-45deg)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', opacity: '0.2' }}>
                        PREVIEW
                    </div>
                </>
            )}
            <Grid container>
                <Grid item sm={10} />
                <Grid item sm={2}>
                    <div 
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', padding: '12px' }}
                        onClick={() => window.open('https://www.monada.ai', '_blank').focus()}
                    >
                        <Typography variant='body' sx={{ whiteSpace: 'nowrap', paddingRight: '3px' }}>
                            Powered by
                        </Typography>
                        <Typography variant='body' style={{ color: '#605DEC', fontSize: '18px', fontFamily: '\'DM Serif Display\', serif', fontWeight: '400' }}>
                                Monada
                        </Typography>
                    </div>
                </Grid>
            </Grid>
            <Box sx={{ padding: '48px' }}>
                <Grid container>
                    <Grid item sm={12} sx={{ display: { xs : 'none', md: 'block' } }}>
                        <div dangerouslySetInnerHTML={{__html: _.get(org, 'headers[\'price-quote\']', '')}} />
                    </Grid>
                    <Grid item xs={12} sx={{ display: { xs : 'block', md: 'none' } }}>
                        <div dangerouslySetInnerHTML={{__html: _.get(org, 'headers[\'price-quote-mobile\']', '')}} />
                    </Grid>
                </Grid>

                <TableContainer sx={{ paddingTop: '24px' }}>
                    <Table>
                        <TableRow>
                            <TableCell colSpan={2} sx={{ fontSize: '14px', fontWeight: 700, borderBottom: '0px', padding: '0px' }}>
                                {opportunity.transportationMethod === 'sea' ? 'Ocean' : opportunity.transportationMethod === 'air' ? 'Air' : 'Land'} Quotation #{readableId(opportunity)}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell colSpan={2} sx={{ fontSize: '14px', fontWeight: 400, borderBottom: '0px', padding: '0px', paddingTop: '12px' }}>
                                To: {account.name}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell colSpan={2} sx={{ fontSize: '14px', fontWeight: 400, borderBottom: '0px', padding: '0px', paddingTop: '12px' }}>
                                Created on: {formatDisplayDateTime(opportunity.created)} 
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{ borderBottom: '0px', padding: '0px', paddingTop: '12px' }}>
                                Incoterms: {INCOTERMS.find(i => i.value === opportunity.incoterm).text}
                            </TableCell>
                        </TableRow>
                    </Table>
                </TableContainer>

                <Grid item sm={12} style={{ paddingBottom: '24px' }}>
                    <Paper style={{ padding: '24px 48px', margin: '24px 0px', ...(print ? { boxShadow: 'none', border: 'none' } : {}) }}>
                        <div dangerouslySetInnerHTML={{ __html: html.contractSummaryTable.replace(/__onclick/g, 'onclick') }} />
                    </Paper>
                </Grid>

                <Grid item sm={12}>
                    <Button sx={{ textTransform: 'none' }}>Download as CSV</Button>
                </Grid>

                <Grid container>
                    <Grid item sm={12}>
                        {((status !== 'completed') && (status !== 'endcustomer-accepted') && (status !== 'endcustomer-rejected')) && (!parsed.print) && (
                            <Button disabled={saving} sx={{ marginTop: saveError === 'select' ? '3px' : '24px', fontSize: '25px !important' }} color='primary' variant='contained' fullWidth onClick={() => onAccept()}>
                                {saving ? <CircularProgress size={25} /> : 'Accept'}
                            </Button>
                        )}
                        {(((status === 'completed') || (status === 'endcustomer-accepted'))) && (!parsed.print) && (
                            <Button disabled={true} sx={{ marginTop: '24px', fontSize: '25px !important' }} color='primary' variant='contained' fullWidth>
                                Price Quote Accepted
                            </Button>
                        )}
                        {(status === 'endcustomer-rejected') && (!parsed.print) && (
                            <Button disabled={true} sx={{ marginTop: '24px', fontSize: '25px !important' }} color='primary' variant='contained' fullWidth>
                                Price Quote Rejected
                            </Button>
                        )}
                        {status !== 'completed' && status !== 'endcustomer-accepted' && status !== 'endcustomer-rejected' && (!parsed.print) && (!saving) && (
                            <p>
                                Please review the offers above. Click <span style={{ color: cyan[500], cursor: 'pointer', textDecoration: 'underline' }} onClick={() => onAccept()}>Accept</span> to select an offer, or click here to <span style={{ color: red[500], cursor: 'pointer', textDecoration: 'underline'}} onClick={() => onReject()}>request a revised offer</span>.
                            </p>
                        )}
                    </Grid>
                    <Grid item sm={2} />
                </Grid>
                <Grid container>
                    <Grid item sm={12} xs={12} style={{ paddingBottom: '16px' }}>
                        <Typography component='div' sx={{ fontSize: '10px', color: '#374151' }}>
                            Created and sent by {opportunity.createdBy}.
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container>
                    {html.terms && (
                        <Grid item sm={12} xs={12} style={{ paddingBottom: '24px', fontSize: '12px' }}>
                            <div dangerouslySetInnerHTML={{ __html: html.terms }} />
                        </Grid>
                    )}
                    <div 
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', padding: '12px' }}
                        onClick={() => window.open('https://www.monada.ai', '_blank').focus()}
                    >
                        <Typography variant='body' sx={{ whiteSpace: 'nowrap', paddingRight: '3px' }}>
                            Powered by
                        </Typography>
                        <Typography variant='body' style={{ color: '#605DEC', fontSize: '18px', fontFamily: '\'DM Serif Display\', serif', fontWeight: '400' }}>
                                Monada
                        </Typography>
                    </div>
                </Grid>
            </Box>
            <Snackbar open={saveError === 'generic'} autoHideDuration={6000} onClose={onSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} >
                <Alert onClose={onSnackbarClose} severity='error' variant='filled' sx={{ width: '100%' }}>
                    Error saving. Please try again.
                </Alert>
            </Snackbar>
            <Snackbar open={saveSuccess && status === 'endcustomer-accepted'} autoHideDuration={6000} onClose={onSnackbarClose}>
                <Alert onClose={onSnackbarClose} severity='success' variant='filled' sx={{ width: '100%' }}>
                    Congrats! You have accepted this price quote.
                </Alert>
            </Snackbar>
            <Snackbar open={saveSuccess && status === 'endcustomer-rejected'} autoHideDuration={6000} onClose={onSnackbarClose}>
                <Alert onClose={onSnackbarClose} severity='success' variant='filled' sx={{ width: '100%' }}>
                    You have rejected this price quote.
                </Alert>
            </Snackbar>
            <Dialog open={showRejectDialog} onClose={() => setShowRejectDialog(null)}>
                <DialogTitle>
                    Reject Offer
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Box>
                            Can you please provide us with the reason you are rejecting our offer?
                        </Box>
                        <Box>
                            <FormControl>
                                <RadioGroup
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                >
                                    <FormControlLabel value='Transit time too long' control={<Radio />} label='Transit time too long' />
                                    <FormControlLabel value='Prefer not to work with this carrier' control={<Radio />} label='Prefer not to work with this carrier' />
                                    <FormControlLabel value='Offer is too expensive' control={<Radio />} label='Offer is too expensive' />
                                    <FormControlLabel value='Other' control={<Radio />} label='Other' />
                                </RadioGroup>
                            </FormControl>
                        </Box>
                        {rejectReason === 'Other' && (
                            <TextField fullWidth label='Can you please tell us the reason?' variant='outlined' value={rejectReasonText} onChange={(e) => setRejectReasonText(e.target.value)} />
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button disabled={!rejectReason || (rejectReason === 'Other' && rejectReasonText.length < 5)} onClick={() => onRejectOK()} autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default OfferOpportunityContract;
